import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENTS = [
  { key: 'new_orders', label: 'New Orders', desc: 'When a new order is placed' },
  { key: 'order_status_changes', label: 'Order Status Changes', desc: 'When order status is updated' },
  { key: 'access_requests', label: 'Access Requests', desc: 'When someone requests or gets outlet access' },
  { key: 'system_updates', label: 'System Updates', desc: 'Platform announcements and updates' },
];

const CHANNELS = [
  { key: 'channel_in_app', label: 'In-App', desc: 'Bell icon notifications' },
  { key: 'channel_push', label: 'Push', desc: 'Browser push notifications' },
  { key: 'channel_email', label: 'Email', desc: 'Email alerts (requires email config)' },
];

const NotificationPreferences = () => {
  const { getAuthHeader } = useAuth();
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  const fetchPrefs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notification-preferences`, { headers: getAuthHeader() });
      setPrefs(res.data);
    } catch (e) {
      console.error('Failed to fetch preferences:', e);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { fetchPrefs(); }, [fetchPrefs]);

  const togglePref = async (key) => {
    const newValue = !prefs[key];
    setSaving(prev => ({ ...prev, [key]: true }));
    try {
      const res = await axios.put(`${API}/notification-preferences`, { [key]: newValue }, { headers: getAuthHeader() });
      setPrefs(res.data);
    } catch (e) {
      toast.error('Failed to update preference');
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  if (loading) {
    return <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>;
  }

  if (!prefs) return null;

  return (
    <div className="space-y-5 pt-4 border-t border-border" data-testid="notification-preferences">
      <div>
        <h3 className="text-sm font-semibold mb-1">Event Preferences</h3>
        <p className="text-xs text-muted-foreground">Choose which events trigger notifications</p>
      </div>
      <div className="space-y-3">
        {EVENTS.map(({ key, label, desc }) => (
          <div key={key} className="flex items-center justify-between" data-testid={`pref-${key}`}>
            <div>
              <Label className="text-sm">{label}</Label>
              <p className="text-xs text-muted-foreground">{desc}</p>
            </div>
            <Switch
              checked={prefs[key] ?? true}
              onCheckedChange={() => togglePref(key)}
              disabled={saving[key]}
              data-testid={`pref-toggle-${key}`}
            />
          </div>
        ))}
      </div>

      <div className="pt-3 border-t border-border">
        <h3 className="text-sm font-semibold mb-1">Delivery Channels</h3>
        <p className="text-xs text-muted-foreground">How you receive notifications</p>
      </div>
      <div className="space-y-3">
        {CHANNELS.map(({ key, label, desc }) => (
          <div key={key} className="flex items-center justify-between" data-testid={`pref-${key}`}>
            <div>
              <Label className="text-sm">{label}</Label>
              <p className="text-xs text-muted-foreground">{desc}</p>
            </div>
            <Switch
              checked={prefs[key] ?? true}
              onCheckedChange={() => togglePref(key)}
              disabled={saving[key]}
              data-testid={`pref-toggle-${key}`}
            />
          </div>
        ))}
      </div>

      <div className="pt-2">
        <div className="flex items-center justify-between" data-testid="pref-sound_enabled">
          <div>
            <Label className="text-sm">Sound Alert</Label>
            <p className="text-xs text-muted-foreground">Play a chime on new notifications</p>
          </div>
          <Switch
            checked={prefs.sound_enabled ?? true}
            onCheckedChange={() => togglePref('sound_enabled')}
            disabled={saving.sound_enabled}
            data-testid="pref-toggle-sound_enabled"
          />
        </div>
      </div>
    </div>
  );
};

export default NotificationPreferences;
